import {
  PropType,
  computed,
  defineComponent,
  ref,
  nextTick,
  watch,
} from 'vue';
import { Toast } from 'vant';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import { ModuleExposeOptions } from '~mall/common/directives/v-expose';
import { Equity, SkuData, SkuDetailData } from '~mall/model/store/modules/ticket';
import { getStylesBinder } from '~mall/common/utils/styles';
import { appQueryConfig } from '~mall/common/utils/url';
// import { appMgr } from '~mall/common/appMgr';
import { reqBuyGoods, BuyType } from '~mall/model/apis/order';
import { uiMgr } from '~mall/common/uiMgr';
import store from '~mall/model/store';
import { isEasyBikeApp, isAliMP, getAppEnv } from '~mall/common/env/env';
import {
  categoryMapArr,
  jumpToCategoryLink,
} from '~mall/common/utils/goMonthlyBike';
import { useActivityBuy } from '~mall/common/utils/useActivityBuy';
import {
  payWithSignNew,
  checkPayWithSignVersionCompatibility,
} from '~mall/model/apis/pay';
import NativeMgr from '~mall/common/native/native';
import openLowVersionModal from '~mall/components/LowVersionModal/index';
import { showThirdPage } from '~mall/common/utils/jumpUrl';
import { getCouponBaseInfo } from '~mall/model/utils';
import { onCashierTrigger } from '~mall/common/utils/widgetGuide';
import styles from './index.module.less';
import {
  splitTitle, formatPrice, renderPrice,
  renderBenefit,
} from '../../util';

const { handleActivityBuy: handleActivityBuyHook } = useActivityBuy();

const cx = getStylesBinder(styles);

  enum LimitStatus {
    /** 无限时抢购 */
    NO = -1,
    /** 已过期 */
    OUTDATED,
    /** 进行中 */
    ONGOING,
    /** 今天开始 */
    TODAY,
    /** 明天开始 */
    TOMORROW,
    /** 明天以后开始 */
    AFTER_TOMORROW,
  }

export default defineComponent({
  name: 'GoodListItemInfo',
  props: {
    skuData: {
      type: Object as PropType<SkuData>,
      required: true,
    },
    limitInfo: {
      type: Number,
      required: true,
    },
    // 超值推荐
    highRecm: {
      type: Boolean,
      default: false,
    },
    /** 购买按钮点击操作处理函数 */
    handleBuy: {
      type: Function as PropType<{ (): void }>,
      required: true,
    },
    /** 奖励金开关状态 */
    rewardSwitchOn: {
      type: Boolean,
      default: false,
    },
  },
  setup: (props) => {
    const buying = ref(false);
    const priceNewRef = ref<HTMLElement | null>(null);
    const priceOriginNewRef = ref<HTMLElement | null>(null);
    const shouldPriceWrap = ref(false);
    const limitStatus = computed(
      () => props.skuData.activityInfo?.timeLimitFlag || LimitStatus.NO,
    );
      /** 类目是否为拼团 */
    const isGroupon = computed(() => props.skuData.categoryId === 'groupon');
    /** 活动售价 */
    const actSalePrice = computed(() => props.skuData.activityInfo?.salePrice);
    /** 限购是否有库存 */
    const hasLimitStock = computed(() => {
      if (props.skuData) {
        return (props.skuData.activityInfo?.saleStock || 0) > 0;
      }

      return false;
    });
      /** 是否能够购买 */
    const canBuy = computed(() => {
      if (!props.skuData) {
        return false;
      }

      const { canPurchase, saleNum } = props.skuData;
      return !!canPurchase && saleNum > 0;
    });
      /** 是否有进行中的活动 */
    const hasOnGoingActivity = computed(
      () => limitStatus.value === LimitStatus.ONGOING && hasLimitStock.value,
    );
      /** 按钮信息 */
    const btnInfo = computed(
      (): {
          text: string;
          type: string;
        } => {
        const { limitInfo } = props;
        let text: string;
        let type: string;

        if (limitInfo === -1) {
          if (props.skuData.saleNum > 0 || isGroupon.value) {
            // * 展示购买按钮
            text = '购买';
            type = 'default';
          } else {
            text = '卖光了';
            type = 'soldout';
          }
        } else if (limitInfo === 1) {
          text = '抢购';
          type = 'limit-now';
        } else {
          text = '开抢';
          type = 'limit-next';
        }

        if (isGroupon.value) {
          text = '拼团购';
        }

        return {
          text,
          type,
        };
      },
    );
      /** 展示现价 */
    const price = computed(() => {
      // 优先展示秒杀价
      if (props.skuData?.effectiveActivityInfo) {
        return props.skuData.effectiveActivityInfo.salePrice;
      }

      if (!actSalePrice.value) {
        if (!isGroupon.value) {
          return props.skuData.salePrice;
        }

        return props.skuData.groupPrice || 0;
      }

      return actSalePrice.value;
    });

    // const realSalePrice = computed(() => {
    // const {
    //   salePrice,
    // promotionInfos,
    // monthlyGoods,
    // monthlyContractChannel,
    // } = props.skuData;
    // 连续包月：支付宝渠道支持 App 和支付宝小程序，其他渠道只支持 App
    /**
     * 连续包月
     * 历史逻辑售价：是用售价 - 优惠价
     * 20260416迭代优惠决策+奖励金抵现需求中，确定连续包月不在进行优惠决策，不会有优惠价
     * 所以去掉连续包月历史算价逻辑
     */
    // if (monthlyGoods) {
    //   const isAlipayChannel = monthlyContractChannel === 'alipay';
    //   const shouldCalculateMonthlyPrice = isAlipayChannel
    //     ? (isEasyBikeApp || isAliMP)
    //     : isEasyBikeApp;
    //   if (shouldCalculateMonthlyPrice) {
    //     const promoPrice = promotionInfos?.[0]?.worth || 0;
    //     return new Fraction(salePrice || 0).sub(promoPrice).valueOf();
    //   }
    // }

    // return salePrice;
    // });

    /**
     * 获取当前展示/下单价格（统一价格计算入口）
     * @return {number} 当前展示/下单价格
     */
    /** 获取当前展示/下单价格（统一价格计算入口） */
    const getPrice = () => {
      // 连续包月：支付宝渠道支持 App 和支付宝小程序，其他渠道只支持 App
      // 连续包月不参加奖励金兑换能力，不进行优惠价格计算
      // if (props.skuData.monthlyGoods) {
      //   const isAlipayChannel = props.skuData.monthlyContractChannel === 'alipay';
      //   const shouldUseMonthlyPrice = isAlipayChannel
      //     ? (isEasyBikeApp || isAliMP)
      //     : isEasyBikeApp;
      //   if (shouldUseMonthlyPrice) {
      //     // 使用连续包月价格
      //     return realSalePrice.value;
      //   }
      // }
      let basePrice = price.value;

      // 优先使用服务端返回的优惠后价格，没有则使用原价兜底
      if (props.skuData.offerPrice != null) {
        // 使用服务端返回的优惠后价格
        basePrice = props.skuData.offerPrice;
      }

      return basePrice;
    };
    /** 公共UBT埋点参数 */
    const commonUbtBizInfo = computed(() => ({
      categoryid: props.skuData.categoryId,
      adsource: appQueryConfig.adsource,
      spuId: props.skuData.spuId,
      marketingLabel: props.skuData.marketingTagName,
      isSupervalue: props.highRecm,
      sku_name: props.skuData.goodsName,
    }));

    const displayOriginPriceTip = () => new Promise<void>((resolve) => {
      Toast('当前是非活动时段，下单前请注意购买金额');
      setTimeout(resolve, 1000);
    });

    const placeOrder = async (type: BuyType) => {
      if (!props.skuData) {
        return;
      }

      //   const {
      //     spuId,
      //     goodsName,
      //     goodsEquitys,
      //     originalPrice,
      //     salePrice,
      //     saleNum,
      //   } = props.skuData;
      //   const { fromTabName, isBikeMall, skuIndex } = router.app.$route.query;
      //   const ubtBizInfo = {
      //     tabname: fromTabName,
      //     isBikeMall,
      //     rank: skuIndex,
      //     adsource: appMgr.pageFrom,
      //     coupon_itemid: spuId,
      //     itemname: goodsName,
      //     coupon_list: (goodsEquitys || [])
      //       .map((item) => item.virtualId)
      //       .join(','),
      //     origin_price: originalPrice,
      //     sale_price: salePrice,
      //     sale_status: saleNum > 0 ? '可购买' : '已售罄',
      //   };
      nextTick(() => {
        // UBT.clickButton({
        //   pageId: 'mkt_couponmall_list',
        //   buttonName: 'mkt_couponmall_list_buy',
        //   categoryId: 'market',
        //   businessInfo: ubtBizInfo,
        // });
      });
      await reqBuyGoods({
        skuData: props.skuData,
        payAmount: getPrice(),
        buyType: type,
        svipAmount: undefined,
        combiProduct: undefined,
        // !!这里传的 highRecm 是历史埋点逻辑，取值为0或1
        highRecm: `${+props.highRecm}`,
      });
    };

    // 购买
    const handleBuy = async () => {
      if (buying.value) {
        return;
      }

      try {
        const buyType = hasOnGoingActivity.value
          ? BuyType.LIMIT
          : BuyType.NORMAL;

        if (buyType === BuyType.NORMAL && !canBuy.value) {
          return;
        }

        if (limitStatus.value > LimitStatus.ONGOING) {
          // 活动还没开始购买商品，给出提示
          await displayOriginPriceTip();
        }

        uiMgr.showLoading();
        await placeOrder(buyType);
      } finally {
        buying.value = false;
      }
    };

    const payWithSignPreCheck = async () => {
      const { version } = await NativeMgr.getDeviceInfo();
      //   UBT.clickButton({
      //     pageId: 'couponmall_list',
      //     buttonName: 'hellomall_itemcardpurchase',
      //     categoryId: 'hellomall',
      //     businessInfo: {
      //       spuIds: props.skuData.spuId,
      //       adsource: appQueryConfig.adsource,
      //       marketingLabel: marketingLabel.value,
      //       version,
      //     },
      //   });

      // 使用抽象的版本检查方法
      if (!checkPayWithSignVersionCompatibility(version) && isEasyBikeApp) {
        openLowVersionModal({
          confirm() {
            // UBT.clickButton({
            //   pageId: 'couponmall_list',
            //   buttonName: 'hellomall_appUpgrade',
            //   categoryId: 'hellomall',
            //   businessInfo: {
            //     spuIds: props.skuData.spuId,
            //     adsource: appQueryConfig.adsource,
            //     marketingLabel: marketingLabel.value,
            //     version,
            //   },
            // });
          },
        });
        return;
      }

      if (!props.skuData) {
        return;
      }
      try {
        // 使用新的 payWithSignNew 方法
        await payWithSignNew({
          ...props.skuData,
          _priceInfo: {
            // 优先使用服务端返回的优惠后价格（含奖励金抵扣），与普通下单 payAmount 逻辑一致
            realPrice: getPrice(),
          },
        } as SkuDetailData & {
            _priceInfo: { realPrice: number };
          });
      } catch (error) {
        // 支付签约失败
      }
    };

    // 列表页---购买按钮点击事件
    const handleClick = (event: MouseEvent) => {
      event.stopPropagation();
      // 购买按钮点击，取消小组件弹窗机会
      onCashierTrigger();
      // 购买按钮点击埋点
      UBT.clickButton({
        pageId: 'platform_couponmall',
        buttonName: 'platform_itemcardpurchase',
        categoryId: 'platform',
        businessInfo: commonUbtBizInfo.value,
      });
      store.commit('skuData/setSkuData', props.skuData);
      // 拼团商品
      if (props.skuData.collageGoods === 1 && props.skuData.collageLink) {
        showThirdPage(props.skuData.collageLink);
        return;
      }
      // 连续包月
      if (props.skuData.monthlyGoods) {
        // 支付宝渠道支持 App 和支付宝小程序，其他渠道只支持 App
        const isAlipayChannel = props.skuData.monthlyContractChannel === 'alipay';
        const shouldPreCheck = isAlipayChannel
          ? (isEasyBikeApp || isAliMP)
          : isEasyBikeApp;
        if (shouldPreCheck) {
          payWithSignPreCheck();
          return;
        }
      }
      // 免密签约
      if (isEasyBikeApp && props.skuData.passwordFreeGoods) {
        payWithSignPreCheck();
        return;
      }
      // app端跳转 (跳转到平台活动落地页中引导用于支付并签约+开通连续包月)， 小程序端正常购买下单
      if (
        isEasyBikeApp
          && categoryMapArr[getAppEnv() as 'fat' | 'uat' | 'pro']?.includes(
            props.skuData.categoryId,
          )
      ) {
        jumpToCategoryLink(
            getAppEnv() as 'fat' | 'uat' | 'pro',
            props.skuData.categoryId,
            props.skuData.spuId,
        );
        return;
      }

      // 限时秒杀购买流程
      if (props.skuData.effectiveActivityInfo) {
        // UBT.clickButton({
        //   pageId: 'couponmall_list',
        //   buttonName: 'couponmall_list_buy',
        //   categoryId: 'hellomall',
        //   businessInfo: {
        //     isflashsalegoods:
        //         router.app.$route.query.newLimit === '1'
        //         || limitStatus.value === LimitStatus.ONGOING
        //           ? '是'
        //           : '否',
        //     isActivityGoods: props.skuData.effectiveActivityInfo ? '1' : '0',
        //     ...sharedUbtBizInfo.value,
        //   },
        // });
        handleActivityBuyHook(
            props.skuData as SkuDetailData,
            props.skuData.categoryId,
        );
        return;
      }

      // AB 固化为直接支付
      handleBuy();
    };

    // 渲染标题，区分数字和非数字样式
    const renderTitle = (title: string) => {
      const parts = splitTitle(title);

      return parts.map((part, index) => (
        <span
          key={index}
          class={cx(part.isNumber ? 'prod-info__title-number' : 'prod-info__title-text')}
        >
          {part.text}
        </span>
      ));
    };

    // 渲染价格，小数部分字号单独处理
    const renderPriceJSX = (price: number) => {
      const { integer, hasDecimal, decimal } = renderPrice(price);

      if (!hasDecimal) {
        return integer;
      }

      return [
        integer,
        <span class={cx('prod-info__price-dot')}>.</span>,
        <span class={cx('prod-info__price-decimal')}>{decimal}</span>,
      ];
    };

    /** 测量价格实际宽度并决定是否换行 */
    const measurePriceWidth = () => {
      nextTick(() => {
        const currentPrice = getPrice();
        const { originalPrice } = props.skuData;

        // 如果没有划线价，不需要换行
        if (originalPrice === currentPrice) {
          shouldPriceWrap.value = false;
          return;
        }

        // 如果当前价格>=5位数（10000及以上），需要换行
        if (currentPrice.toString().length >= 6) {
          shouldPriceWrap.value = true;
          return;
        }

        // 获取实际渲染的元素宽度
        if (priceNewRef.value && priceOriginNewRef.value) {
          const currentPriceWidth = priceNewRef.value.offsetWidth;
          const originalPriceWidth = priceOriginNewRef.value.offsetWidth;
          const totalWidth = currentPriceWidth + originalPriceWidth + 4; // 4px是margin-left

          // logger('实际测量 -', props.skuData.goodsName, {
          //   currentPriceWidth,
          //   originalPriceWidth,
          //   totalWidth,
          //   shouldWrap: totalWidth > 80,
          // });

          shouldPriceWrap.value = totalWidth > 80;
        } else {
          // 如果元素还没有渲染，使用快速估算作为后备
          const currentPriceStr = currentPrice.toString();
          shouldPriceWrap.value = currentPrice >= 10000 || currentPriceStr.length >= 5;
        }
      });
    };

    // 监听价格变化，重新测量
    watch(() => [getPrice(), props.skuData.originalPrice], () => {
      measurePriceWidth();
    }, { immediate: true });

    /** 模块曝光选项 */
    const buyBtnUbtExpose = computed<ModuleExposeOptions>(() => ({
      exposeData: {
        pageId: 'platform_couponmall',
        categoryId: 'platform',
        moduleId: 'platform_itemcardpurchase',
        contentId: 'platform_itemcardpurchase',
        exposureTimes: 1,
        businessInfo: commonUbtBizInfo.value,
      },
    }));


    return () => {
      const {
        goodsName, originalPrice, goodsTags,
      } = props.skuData;

      // 获取权益渲染信息
      const benefitInfo = renderBenefit(props.skuData);
      const {
        couponPackage, couponList = [], equityCount, hasMore,
      } = benefitInfo;
      const singleCoupon = couponList?.length === 1;

      return (
          <div style={'flex:1;'}>
            <div class={cx('prod-info-new')}>
              <div class={cx('prod-info-left')}>
                {couponPackage ? (
                  // 优惠券包样式
                  <div
                    class={cx('coupon-package', {
                      'coupon-package_single': singleCoupon,
                    })}
                  >
                    <div
                      class={cx('coupon-list', {
                        'coupon-list_single': singleCoupon,
                      })}
                    >
                      {couponList.map(
                        (coupon: Equity, index: number) => {
                          const { num, word } = getCouponBaseInfo(coupon);
                          return (
                            <div key={index} class={cx('coupon-item')}>
                              <div class={cx('coupon-value-line')}>
                                <span class={cx('coupon-worth')}>
                                  {num}
                                  <span class={cx('coupon-unit')}>{word}</span>
                                </span>
                                <span class={cx('coupon-times')}>×</span>
                                <span class={cx('coupon-num')}>
                                  {coupon.virtualNum}
                                  <span class={cx('coupon-unit')}>张</span>
                                </span>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                    {hasMore && (
                      <span class={cx('coupon-more-wrap')}>—— <span class={cx('coupon-more')}>共{equityCount}张</span> ——</span>
                    )}
                  </div>
                ) : (
                  // 普通商品样式
                  <div class={cx('prod-info__normal-goods')}>
                    <div class={cx('prod-info__title-new')}>{renderTitle(goodsName)}</div>
                    {goodsTags && goodsTags.length > 0 && (<div class={cx('prod-info__benefit-listnew')}>
                      {goodsTags?.slice(0, 2).map((item: string, index: number) => (
                        <div
                          key={item}
                          class={cx(
                            index === 0 ? 'prod-info__benefit-tag_first' : 'prod-info__benefit-tag_second',
                          )}
                        >
                          {item}
                        </div>
                      ))}
                    </div>)}
                  </div>
                )}
              </div>
              <div class={cx('prod-info-right')}>
                <div
                  class={cx('prod-info__price-container', {
                    'prod-info__price-container_wrap': shouldPriceWrap.value,
                  })}
                >
                  <div
                    ref={priceNewRef}
                    class={cx('prod-info__price-new', 'prod-info__price_curr', {
                      'prod-info__price_curr_limit': props.limitInfo !== -1,
                    })}
                  >
                    {renderPriceJSX(getPrice())}
                  </div>
                  {originalPrice !== getPrice() && (
                    <div ref={priceOriginNewRef} class={cx('prod-info__price_originnew')}>
                      {formatPrice(originalPrice)}
                    </div>
                  )}
                </div>
                <div
                  class={cx(
                    'prod-info__btnnew',
                    `prod-info__btn_${btnInfo.value.type}`,
                  )}
                  onClick={handleClick}
                  v-node-expose={buyBtnUbtExpose.value}
                >
                  {btnInfo.value.text}
                </div>
              </div>
            </div>
          </div>
      );
    };
  },
});
